import React from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { Traveler, TravelerProfile } from '@marageti/z4-sdk/lib/people';
import { TripRequest, TripMatch, Review } from '@marageti/z4-sdk/lib/travel';
import { SearchResult } from '@marageti/z4-sdk/lib/entity';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import MergeTravelersView from './merge-travelers-view';
import { TravelerData } from './types';

const createTravelerData = (
  travelerResponse: any,
  tripRequests: SearchResult<TripRequest> | undefined,
  tripMatches: SearchResult<TripMatch> | undefined,
  reviews: SearchResult<Review> | undefined,
  travelerProfile: TravelerProfile | undefined,
): TravelerData => ({
  traveler: travelerResponse.data,
  loading: travelerResponse.isLoading,
  error: travelerResponse.error,
  tripRequests,
  tripMatches,
  reviews,
  travelerProfile,
});

const MergeTravelersContainer = () => {
  const apiClient = useApiClient();
  const [email1, setEmail1] = React.useState('');
  const [email2, setEmail2] = React.useState('');
  const [mergeResults, setMergeResults] = React.useState<string[]>([]);
  const [isMerging, setIsMerging] = React.useState(false);
  const [submissionError, setSubmissionError] = React.useState<ApiError>();

  const traveler1Response = useFetch<Traveler>(
    apiClient.peopleClient.getTraveler,
    [email1],
    !!email1.trim(),
  );

  const traveler2Response = useFetch<Traveler>(
    apiClient.peopleClient.getTraveler,
    [email2],
    !!email2.trim(),
  );

  const { data: tripRequests1 } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [traveler1Response?.data?.id || ''] }],
      sortBy: [{ field: 'departureDate', order: 'asc' }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler1Response?.data?.id,
  );

  const { data: tripMatches1 } = useFetch<SearchResult<TripMatch>>(
    apiClient.tripMatchesClient.postTripMatchSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [traveler1Response?.data?.id || ''] }],
      sortBy: [{ field: 'departureDate', order: 'asc' }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler1Response?.data?.id,
  );

  const { data: reviews1 } = useFetch<SearchResult<Review>>(
    apiClient.reviewsClient.postReviewsSearch,
    [{
      filterBy: [{ field: 'travelerId', value: [traveler1Response?.data?.id || ''] }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler1Response?.data?.id,
  );

  const { data: travelerProfile1 } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [traveler1Response?.data?.id || ''],
    !!traveler1Response?.data?.id,
  );

  const { data: tripRequests2 } = useFetch<SearchResult<TripRequest>>(
    apiClient.tripRequestsClient.postTripRequestSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [traveler2Response?.data?.id || ''] }],
      sortBy: [{ field: 'departureDate', order: 'asc' }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler2Response?.data?.id,
  );

  const { data: tripMatches2 } = useFetch<SearchResult<TripMatch>>(
    apiClient.tripMatchesClient.postTripMatchSearches,
    [{
      filterBy: [{ field: 'travelers.id', value: [traveler2Response?.data?.id || ''] }],
      sortBy: [{ field: 'departureDate', order: 'asc' }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler2Response?.data?.id,
  );

  const { data: reviews2 } = useFetch<SearchResult<Review>>(
    apiClient.reviewsClient.postReviewsSearch,
    [{
      filterBy: [{ field: 'travelerId', value: [traveler2Response?.data?.id || ''] }],
      pagination: { limit: 10000, page: 1 },
    }],
    !!traveler2Response?.data?.id,
  );

  const { data: travelerProfile2 } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [traveler2Response?.data?.id || ''],
    !!traveler2Response?.data?.id,
  );

  const traveler1Data: TravelerData = createTravelerData(
    traveler1Response,
    tripRequests1,
    tripMatches1,
    reviews1,
    travelerProfile1,
  );

  const traveler2Data: TravelerData = createTravelerData(
    traveler2Response,
    tripRequests2,
    tripMatches2,
    reviews2,
    travelerProfile2,
  );

  const mergeTravelers = async (): Promise<string[]> => {
    if (!traveler1Data.traveler?.id || !traveler2Data.traveler?.id) {
      setIsMerging(false);
      return [];
    }

    try {
      const result = await apiClient.peopleClient.putTravelerMergeAccounts(traveler1Data.traveler?.id, traveler2Data.traveler?.id);
      return result;
    } catch (error) {
      setSubmissionError(error as ApiError);
      return [];
    } finally {
      setIsMerging(false);
    }
  };

  const canMerge = !!(traveler1Data.traveler && traveler2Data.traveler) && traveler1Data.traveler.id !== traveler2Data.traveler.id;

  return (
    <MergeTravelersView
      traveler1Data={traveler1Data}
      traveler2Data={traveler2Data}
      mergeResults={mergeResults}
      isMerging={isMerging}
      mergeTravelers={mergeTravelers}
      setMergeResults={setMergeResults}
      setIsMerging={setIsMerging}
      canMerge={canMerge}
      submissionError={submissionError}
      setSubmissionError={setSubmissionError}
      email1={email1}
      email2={email2}
      setEmail1={setEmail1}
      setEmail2={setEmail2}
    />
  );
};

export default MergeTravelersContainer;
